import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Shared textarea primitive, for the Daily Check-in's optional note.
 *
 * The classes are `Input`'s, minus the ones only a single-line field can use
 * (`h-9`, the file-input rules) — the same border, background, placeholder and
 * focus ring, so a note field and a text field are the same control at
 * different heights.
 *
 * `text-base md:text-sm` is carried over deliberately rather than copied
 * loosely: 16px on mobile is what stops iOS Safari zooming the viewport when
 * the field takes focus (visual system, section D.7).
 *
 * `focus-visible:ring-1 focus-visible:ring-ring` is mandatory (section E.7).
 */
export type TextareaProps = React.ComponentProps<'textarea'>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'flex w-full rounded-md border border-input bg-transparent px-3 py-2 ' +
          'text-base shadow-sm transition-colors ' +
          'placeholder:text-muted-foreground focus-visible:outline-none ' +
          'focus-visible:ring-1 focus-visible:ring-ring ' +
          'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        className,
      )}
      {...props}
    />
  ),
);

Textarea.displayName = 'Textarea';
